import { Auth } from "aws-amplify";
import { Formik, Form } from "formik";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Yup from "yup";
import { isEmpty, get } from "lodash";
import ToastMessage from "../../components/molecules/ToastMessage";
import { SPINNER_NAMES } from "../../components/organisms/Spinner";
import { actions as spinnerActions } from "../../components/organisms/Spinner/spinner.reducers";
import { logoIcon } from "../../assets/icons";
import { setPasswordBg } from "../../assets/images";
import { Alert, OtpInput } from "../../components/molecules";
import {
  EMPTY_STRING,
  REGEX,
  ROUTES,
  USER_INFO,
  VALIDATION_MESSAGES,
  CACHE_KEYS,
  LOGIN_ERROR_MESSAGE,
} from "../../constants";
import UpdatePassword from "./UpdatePassword";
import { fetchInvitations } from "../Users/users.actions";
import { actions as userActions } from "../UserProfile/userProfile.reducers";
import {
  fetchUserProfileById,
  updateUserProfile,
  setIsAuthenticated,
} from "../UserProfile";
import { getDecryptedSecretKey } from "../../helpers";
import { captureMessage } from "@sentry/react";
import { v4 as uuid } from "uuid";

const { addSpinner, removeSpinner } = spinnerActions;
const { PASSWORD } = REGEX;
const { LOGIN, AUTH_CONFIRMATION } = ROUTES;
const INVITE = "invite";
const { SET_PASSWORD } = SPINNER_NAMES;
const keyValue = "default";
const {
  FIELD_EMPTY,
  MIN_PASSWORD_LENGTH,
  PASSWORD_SHOULD_CONTAINS,
  VALIDATE_PASSWORD,
  NEW_PASS,
  MATCH_PASSWORD,
} = VALIDATION_MESSAGES;
const { setShouldFetchUser } = userActions;
const { X_REQUEST_ID } = CACHE_KEYS;

const SetPassword = ({ setIsSigningUp }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const url = new URLSearchParams(window.location.search);
  const location = useLocation();
  const [alertData, setAlertData] = useState();
  const [shouldShowNotification, setShouldShowNotification] = useState(false);
  const [isInvitedUser, setIsInvitedUser] = useState(false);
  const [isAccepted, setIsAccepted] = useState(false);
  const [isInvitationValid, setIsInvitationValid] = useState(true);
  const [invitationInfo, setInvitationInfo] = useState(null);
  const [isOtpValid, setIsOtpValid] = useState(true);

  const userDetails = {
    newPassword: EMPTY_STRING,
    confirmPassword: EMPTY_STRING,
    authCode: {
      one: EMPTY_STRING,
      two: EMPTY_STRING,
      three: EMPTY_STRING,
      four: EMPTY_STRING,
      five: EMPTY_STRING,
      six: EMPTY_STRING,
    },
  };

  const proxy = new Proxy(url, {
    get: (searchParams, prop) => searchParams.get(prop),
  });
  const urlType = get(proxy, "type");
  const encryptedOrganizationId = decodeURIComponent(proxy.organizationId);
  const organizationId = getDecryptedSecretKey(encryptedOrganizationId);
  const decodedEmail = decodeURIComponent(proxy.email);

  useEffect(() => {
    if (urlType === INVITE) {
      dispatch(fetchInvitations({ email: encodeURIComponent(decodedEmail), organizationId }))
        .then(async (res) => {
          if (res.payload) {
            setIsAccepted(res.payload.status === "ACCEPTED")
            setInvitationInfo(res.payload);
            const currentDate =  new Date().toISOString();
            setIsInvitationValid(res.payload.expireAt >= currentDate);
          }
        })
        .catch((error) => {
          setAlertData({ message: error.message });
        });
    }
  }, [dispatch, urlType, decodedEmail, organizationId]);

  useEffect(() => {
    if ((location.key === keyValue && setIsInvitedUser(urlType === INVITE)) || isAccepted)
      navigate(LOGIN);
    setIsInvitedUser(urlType === INVITE);
  }, [urlType, navigate, location.key, isAccepted]);

  useEffect(() => {
    if (!isInvitationValid)
      setTimeout(() => {
        redirectOnLoginPage();
      }, "5000");
  });

  //Need to add the DB call to get the information
  const redirectOnLoginPage = () => navigate(LOGIN, { replace: true });

  const validationSchema = Yup.object({
    newPassword: Yup.string()
      .required(FIELD_EMPTY)
      .min(8, MIN_PASSWORD_LENGTH)
      .test(
        VALIDATE_PASSWORD,
        PASSWORD_SHOULD_CONTAINS,
        (val) => val && PASSWORD.test(val)
      ),
    confirmPassword: Yup.string()
      .required(FIELD_EMPTY)
      .oneOf([Yup.ref(NEW_PASS), null], MATCH_PASSWORD),
    ...(!isInvitedUser && {
      authCode: Yup.object().shape(
        Object.keys(userDetails.authCode).reduce(
          (acc, val) => ({ ...acc, [val]: Yup.string().required() }),
          {}
        )
      ),
    }),
  });

  const handleOnSubmit = (values) => {
    const { newPassword, authCode } = values;
    const code = Object.values(authCode).reduce(
      (acc, val) => acc + val,
      EMPTY_STRING
    );
    dispatch(addSpinner(SET_PASSWORD));
    isInvitedUser
      ? Auth.signUp({
          username: decodedEmail,
          password: values.newPassword,
          attributes: { email: decodedEmail },
          autoSignIn: { enabled: true },
        })
          .then((res) => {
            let userInfo = {
              sub: res.userSub,
              name: invitationInfo.username,
              invitedBy: invitationInfo.invitedBy,
              userId: invitationInfo.userId,
              email: decodedEmail,
              inviteId: invitationInfo.id,
              organization: EMPTY_STRING,
              profilePicUrl: EMPTY_STRING,
            };
            setIsSigningUp(true);
            sessionStorage.setItem(USER_INFO, JSON.stringify(userInfo));
            navigate(AUTH_CONFIRMATION);
          })
          .catch((error) => {
            dispatch(removeSpinner(SET_PASSWORD));
            setAlertData({ message: error.message });
          })
      : Auth.forgotPasswordSubmit(decodedEmail, code, newPassword)
          .then(() => {
            setShouldShowNotification(true);
            dispatch(addSpinner(SET_PASSWORD));
            Auth.signIn(decodedEmail, newPassword)
            .then((res) => {
              dispatch(fetchUserProfileById(res.attributes.sub)).then((res) => {
                if (!isEmpty(res.payload)) {
                  dispatch(
                    updateUserProfile({
                      id: res.payload.id,
                      user: {
                        lastSignIn: new Date(),
                      },
                    })
                  ).then(() => {
                    dispatch(setShouldFetchUser(false));
                    dispatch(setIsAuthenticated(true));
                    sessionStorage.setItem(X_REQUEST_ID, uuid());
                    dispatch(removeSpinner(SET_PASSWORD));
                  });
                } else setAlertData({ message: LOGIN_ERROR_MESSAGE });
              });
            })
            .catch((err) => {
              setAlertData({ message: err.message });
              const { pathname } = location;
              const detailedErrorInfo = {
                url: pathname,
                updatedBody: values,
              };
              captureMessage(
                `${err.message}\n${JSON.stringify(detailedErrorInfo)}`
              );
              redirectOnLoginPage();
            });
            dispatch(removeSpinner(SET_PASSWORD));
          })
          .catch((error) => {
            dispatch(removeSpinner(SET_PASSWORD));
            setAlertData({ message: error.message });
            setIsOtpValid(false);
          });
  };

  return (
    <>
      {(location.key !== keyValue || urlType === INVITE) && (
        <>
            <div className="auth-container min-h-screen relative flex items-center">
              <img
                src={setPasswordBg}
                className="w-full h-full absolute top-0 left-0 object-cover pointer-events-none"
                alt="set-password-bg"
              />
              <div className="auth-box text-gray-50 relative w-full mx-auto py-12 px-6 sm:w-1/2 md:w-1/2 lg:w-1/3 2xl:w-1/4">
                <Formik
                  initialValues={userDetails}
                  validationSchema={validationSchema}
                  onSubmit={handleOnSubmit}
                >
                  {({
                    values,
                    touched,
                    errors,
                    setFieldValue,
                    handleChange,
                    handleBlur,
                    isValid,
                    dirty,
                  }) => (
                    <Form>
                      {isInvitationValid && (
                        <div>
                          <div className="auth-header mb-8">
                            {alertData && (
                              <div className="my-2">
                                <Alert
                                  type={alertData.type}
                                  message={alertData.message}
                                  handleOnClose={setAlertData}
                                />
                              </div>
                            )}
                            <div className="logo mb-6">
                              <img src={logoIcon} width={52} alt="logo-icon" />
                            </div>
                            <h1 className="text-3xl leading-9 font-extrabold mb-2">
                              Set New Password
                            </h1>
                            {!isInvitedUser && (
                              <span className="text-6">
                                Or&nbsp;
                                <Link
                                  to={LOGIN}
                                  className="text-indigo-200 hover:text-indigo-500"
                                >
                                  login instead
                                </Link>
                              </span>
                            )}
                          </div>
                          {!isInvitedUser && (
                            <div className="form-group">
                              <label className="text-sm font-medium">
                                Enter Verification Code
                              </label>
                              <div className="my-4 flex justify-between">
                                <OtpInput
                                  values={values}
                                  setFieldValue={setFieldValue}
                                  isOtpValid={isOtpValid}
                                  setIsOtpValid={setIsOtpValid}
                                />
                              </div>
                              <div className="flex justify-end">
                                <span className="text-6 text-gray-300 text-xs italic">
                                  Check email for verification code.
                                </span>
                              </div>
                              {touched.code && errors.code && (
                                <div className="font-medium text-red-500 text-sm absolute">
                                  {errors.code}
                                </div>
                              )}
                            </div>
                          )}
                          {
                            <UpdatePassword
                              values={values}
                              handleBlur={handleBlur}
                              handleChange={handleChange}
                              touched={touched}
                              errors={errors}
                              isValid={isValid}
                              dirty={dirty}
                            />
                          }
                        </div>
                      )}
                      {!isInvitationValid && urlType === INVITE && (
                        <div className="auth-body">
                          <div className="logo mb-6">
                            <img src={logoIcon} width={52} alt="logo-icon" />
                          </div>
                          <h1 className="text-3xl leading-9 font-extrabold mb-2">
                            Invite Expired
                          </h1>

                          <div className="flex items-center">
                            <span className="text-6 text-gray-300 text-xs">
                              Please contact your admin for another invite link.
                              Redirecting you to login page.
                            </span>
                          </div>
                        </div>
                      )}
                    </Form>
                  )}
                </Formik>
              </div>
            </div>
          {shouldShowNotification && (
            <ToastMessage
              notificationMessage={"Successfully updated password."}
              setShouldShowNotification={setShouldShowNotification}
            />
          )}
        </>
      )}
    </>
  );
};

export default SetPassword;
